import React, { useEffect, useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { market } from '../services/api';

interface Props {
  holdings: Holding[];
}

export const RebalancingAdvisor: React.FC<Props> = ({ holdings }) => {
  const { t } = useLanguage();
  const [eurUsdRate, setEurUsdRate] = useState(1.09);

  useEffect(() => {
    market.getFxRate()
      .then(res => { if (res.data?.rate) setEurUsdRate(res.data.rate); })
      .catch(() => {});
  }, []);

  // Convert a raw price (which may be in USD or EUR) to EUR
  const toEur = (price: number, currency?: string) =>
    currency === 'EUR' ? price : price / eurUsdRate;

  const totalValue = holdings.reduce((sum, h) => {
    const price = toEur(h.current_price || h.avg_cost || 0, h.currency);
    return sum + h.quantity * price;
  }, 0);

  // Equal-weight target across all holdings (no hardcoded symbols)
  const recommendations = holdings.map(h => {
    const priceEur = toEur(h.current_price || h.avg_cost || 1, h.currency);
    const valueEur = h.quantity * priceEur;
    const currentWeight = totalValue > 0 ? (valueEur / totalValue) * 100 : 0;
    const targetWeight = 100 / (holdings.length || 1);
    const weightDiff = targetWeight - currentWeight;
    const targetValue = (targetWeight / 100) * totalValue;
    const valueDiff = targetValue - valueEur;
    const qtyDiff = valueDiff / priceEur;

    return {
      symbol: h.symbol,
      currentWeight,
      targetWeight,
      weightDiff,
      valueDiff,
      qtyDiff,
    };
  }).filter(r => Math.abs(r.weightDiff) > 1.5);

  return (
    <div className="glass-panel p-6 space-y-4">
      <div>
        <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917]">
          {t("rebalanceTitle")}
        </h3>
        <p className="text-[10px] text-stone-500 font-mono mt-0.5">
          {t("rebalanceDesc")}
        </p>
      </div>

      {recommendations.length === 0 ? (
        <div className="text-xs text-stone-500 font-mono py-2 bg-stone-100/50 border border-dashed border-stone-300 rounded text-center">
          {t("rebalanceEmpty")}
        </div>
      ) : (
        <div className="space-y-3">
          {recommendations.map((r) => {
            const isBuy = r.qtyDiff > 0;
            return (
              <div
                key={r.symbol}
                className={`p-3 rounded border text-xs font-mono flex flex-col md:flex-row justify-between items-start md:items-center gap-2 ${
                  isBuy
                    ? 'bg-emerald-50/50 border-emerald-500/30'
                    : 'bg-rose-50/50 border-rose-500/30'
                }`}
              >
                <div>
                  <span className={`font-extrabold px-1.5 py-0.5 rounded text-[10px] text-white uppercase mr-2 ${isBuy ? 'bg-emerald-700' : 'bg-rose-700'}`}>
                    {isBuy ? t("buyLabel") : t("sellLabel")}
                  </span>
                  <span className="font-extrabold text-[#1c1917]">{r.symbol}</span>
                  <span className="text-stone-500 ml-2">
                    Current: {r.currentWeight.toFixed(1)}% → Target: {r.targetWeight.toFixed(1)}%
                  </span>
                </div>
                <div className="text-right font-extrabold">
                  <span className={isBuy ? 'text-emerald-700' : 'text-rose-700'}>
                    {isBuy ? '+' : ''}{Math.abs(r.qtyDiff).toFixed(2)} {t("rebalanceUnits")}
                  </span>
                  <span className="text-stone-400 font-normal text-[10px] ml-2">
                    ({isBuy ? '+' : '-'}€{Math.abs(r.valueDiff).toLocaleString(undefined, { maximumFractionDigits: 0 })})
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
